import { useEffect, useMemo, useState } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ComposedChart,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { apiService, type AnalyticsFilterParams, type AnalyticsOverview as OverviewData } from "../../services/api.service";
import { Button } from "../ui/button";
import { AnalyticsFilters, type FilterDisplayState } from "./AnalyticsFilters";
import { ChartCard, MetricCard, SectionHeader } from "./AnalyticsUI";
import { formatCompactNumber, formatPercent, formatSummaryStat, mean, percentile } from "./analyticsFormatters";
import { ANALYTICS_COLORS, ANALYTICS_SERIES } from "./analyticsTheme";

// Grafana-style stat legend table rendered below a chart
interface StatSeries {
    name: string;
    color: string;
    values: number[];
}
interface StatLegendTableProps {
    series: StatSeries[];
    columns: { label: string; p?: number; stat?: "mean" | "last" }[];
}
function StatLegendTable({ series, columns }: StatLegendTableProps) {
    if (!series.length || !series[0].values.length) return null;
    return (
        <div className="mt-3 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
                <thead>
                    <tr className="border-b border-border">
                        <th className="py-1 pr-3 text-left font-medium text-muted-foreground">Series</th>
                        {columns.map((col) => (
                            <th key={col.label} className="py-1 px-2 text-right font-medium text-muted-foreground">{col.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {series.map((s) => {
                        const vals = s.values.filter(Number.isFinite);
                        return (
                            <tr key={s.name} className="border-b border-border/40 last:border-0">
                                <td className="py-1 pr-3 flex items-center gap-1.5 whitespace-nowrap">
                                    <span className="inline-block w-3 h-0.5 rounded-full" style={{ backgroundColor: s.color }} />
                                    <span className="text-foreground">{s.name}</span>
                                </td>
                                {columns.map((col) => {
                                    let val = 0;
                                    if (col.stat === "mean") val = mean(vals);
                                    else if (col.stat === "last") val = vals[vals.length - 1] ?? 0;
                                    else if (col.p !== undefined) val = percentile(vals, col.p);
                                    return (
                                        <td key={col.label} className="py-1 px-2 text-right tabular-nums text-foreground">
                                            {Number.isFinite(val) ? Math.round(val * 10) / 10 : "-"}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

interface Props {
    filters: AnalyticsFilterParams;
    onFiltersChange: (f: AnalyticsFilterParams) => void;
    filterDisplay: FilterDisplayState;
    onFilterDisplayChange: (d: FilterDisplayState) => void;
}

export const AnalyticsEngagement = ({ filters, onFiltersChange, filterDisplay, onFilterDisplayChange }: Props) => {
    const [data, setData] = useState<OverviewData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadData = async (params: AnalyticsFilterParams = {}) => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await apiService.getAnalyticsOverview(params);
            setData(result);
        } catch {
            setError("Failed to load analytics overview");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData(filters);
    }, []);

    const handleFilterApply = (newFilters: AnalyticsFilterParams) => {
        onFiltersChange(newFilters);
        loadData(newFilters);
    };

    const dailySummary = useMemo(() => {
        if (!data?.daily_trend.length) return [];
        const sessions = data.daily_trend.map((row) => row.sessions);
        return [
            formatSummaryStat("mean", mean(sessions)),
            formatSummaryStat("p95", percentile(sessions, 95)),
        ];
    }, [data]);

    const heroChartData = useMemo(() => {
        if (!data?.daily_trend.length) return [];
        const period = 7;
        const alpha = 2 / (period + 1); // EMA multiplier for period=7
        let ema: number | null = null;
        return data.daily_trend.map((row, i) => {
            // 7-day SMA: average of last 7 values (null for first 6)
            const sma = i >= period - 1
                ? data.daily_trend.slice(i - period + 1, i + 1).reduce((s, r) => s + r.avg_completion, 0) / period
                : null;
            // 7-day EMA: seed with first value, then apply exponential smoothing
            if (ema === null) {
                ema = row.avg_completion;
            } else {
                ema = row.avg_completion * alpha + ema * (1 - alpha);
            }
            return {
                ...row,
                sma: sma !== null ? Math.round(sma * 10) / 10 : null,
                ema: Math.round(ema * 10) / 10,
            };
        });
    }, [data]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>Loading analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <p>{error}</p>
                <Button variant="outline" className="mt-4" onClick={() => loadData(filters)}>Retry</Button>
            </div>
        );
    }

    const sessionsPerStudent = data.total_students === 0
        ? 0
        : Math.round((data.total_sessions / data.total_students) * 10) / 10;

    return (
        <div className="space-y-6">
            <SectionHeader
                title="Engagement"
                description="This tab sets the visual language for the analytics experience and gives a quick read on volume, pace, and completion."
            />

            <AnalyticsFilters filters={filters} onApply={handleFilterApply} filterDisplay={filterDisplay} onFilterDisplayChange={onFilterDisplayChange} />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
                <MetricCard
                    title="Completion Rate"
                    value={formatPercent(data.avg_completion_pct)}
                    hint="Average completion across all sessions."
                    tone={data.avg_completion_pct >= 75 ? "success" : data.avg_completion_pct >= 40 ? "warning" : "danger"}
                    trend={data.daily_trend.map((row) => ({ label: row.date, value: row.avg_completion }))}
                />
                <MetricCard
                    title="Total Sessions"
                    value={formatCompactNumber(data.total_sessions)}
                    hint="Total learning sessions in the selected window."
                    tone="info"
                    trend={data.daily_trend.map((row) => ({ label: row.date, value: row.sessions }))}
                    summaries={dailySummary}
                />
                <MetricCard
                    title="Unique Lessons"
                    value={formatCompactNumber(data.unique_lessons ?? 0)}
                    hint="Distinct lessons accessed in the selected window."
                    tone="neutral"
                />
                <MetricCard
                    title="Sessions / Student"
                    value={sessionsPerStudent.toFixed(1)}
                    hint="Average sessions per active student."
                    tone="neutral"
                />
                <MetricCard
                    title="Active Students"
                    value={formatCompactNumber(data.total_students)}
                    hint="Unique students active in the selected window."
                    tone="neutral"
                />
            </div>

            <ChartCard
                title="Completion Rate & Sessions Over Time"
                description="Session volume on the right axis; completion rate and smoothing lines on the left."
            >
                {heroChartData.length > 0 ? (
                    <>
                        <ResponsiveContainer width="100%" height={320}>
                            <ComposedChart data={heroChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis
                                    yAxisId="left"
                                    tick={{ fontSize: 11 }}
                                    domain={[0, 100]}
                                    label={{ value: "Completion %", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                                />
                                <YAxis
                                    yAxisId="right"
                                    orientation="right"
                                    tick={{ fontSize: 11 }}
                                    label={{ value: "Sessions", angle: 90, position: "insideRight", style: { fontSize: 11 } }}
                                />
                                <Tooltip />
                                <Bar yAxisId="right" dataKey="sessions" name="Sessions" fill={ANALYTICS_SERIES.sessions} radius={[4, 4, 0, 0]} />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="avg_completion"
                                    name="Completion %"
                                    stroke={ANALYTICS_SERIES.completion}
                                    strokeWidth={2}
                                    dot={false}
                                />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="sma"
                                    name="7-day SMA"
                                    stroke="#f97316"
                                    strokeWidth={1.5}
                                    strokeDasharray="5 3"
                                    dot={false}
                                    connectNulls
                                />
                                <Line
                                    yAxisId="left"
                                    type="monotone"
                                    dataKey="ema"
                                    name="7-day EMA"
                                    stroke="#a855f7"
                                    strokeWidth={1.5}
                                    strokeDasharray="2 3"
                                    dot={false}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <StatLegendTable
                            series={[
                                { name: "Completion %", color: ANALYTICS_SERIES.completion, values: heroChartData.map((r) => r.avg_completion) },
                                { name: "Sessions", color: ANALYTICS_SERIES.sessions, values: heroChartData.map((r) => r.sessions) },
                                { name: "7-day SMA", color: "#f97316", values: heroChartData.filter((r) => r.sma !== null).map((r) => r.sma as number) },
                                { name: "7-day EMA", color: "#a855f7", values: heroChartData.map((r) => r.ema) },
                            ]}
                            columns={[
                                { label: "Mean", stat: "mean" },
                                { label: "Last", stat: "last" },
                                { label: "p25", p: 25 },
                                { label: "p50", p: 50 },
                                { label: "p75", p: 75 },
                                { label: "p90", p: 90 },
                                { label: "p95", p: 95 },
                            ]}
                        />
                    </>
                ) : (
                    <p className="py-12 text-center text-muted-foreground">No data for this period</p>
                )}
            </ChartCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ChartCard
                    title="Started vs Completed"
                    description="Each bar is total sessions started; the green portion shows how many completed."
                >
                    {data.daily_trend.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={data.daily_trend.map((r) => ({ ...r, not_completed: r.sessions - r.completed }))}>
                                <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Bar dataKey="completed" name="Completed" stackId="a" fill={ANALYTICS_SERIES.completion} />
                                <Bar dataKey="not_completed" name="Not Completed" stackId="a" fill={ANALYTICS_COLORS.muted} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No data for this period</p>
                    )}
                </ChartCard>

                <ChartCard
                    title="Avg Interactions Per Session"
                    description="Daily average tutor messages, student messages, and QA responses per session."
                >
                    {(data.interactions_trend ?? []).length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={data.interactions_trend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={ANALYTICS_COLORS.grid} />
                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="avg_tutor_msgs"
                                        name="Tutor Messages"
                                        stroke={ANALYTICS_SERIES.sessions}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="avg_student_msgs"
                                        name="Student Messages"
                                        stroke={ANALYTICS_SERIES.completion}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                            <StatLegendTable
                                series={[
                                    { name: "Tutor Messages", color: ANALYTICS_SERIES.sessions, values: data.interactions_trend.map((r) => r.avg_tutor_msgs) },
                                    { name: "Student Messages", color: ANALYTICS_SERIES.completion, values: data.interactions_trend.map((r) => r.avg_student_msgs) },
                                ]}
                                columns={[
                                    { label: "Mean", stat: "mean" },
                                    { label: "p5", p: 5 },
                                    { label: "p25", p: 25 },
                                    { label: "p50", p: 50 },
                                    { label: "p75", p: 75 },
                                ]}
                            />
                        </>
                    ) : (
                        <p className="py-12 text-center text-muted-foreground">No data for this period</p>
                    )}
                </ChartCard>
            </div>
        </div>
    );
};
